import styled, { css } from 'styled-components'

import Icon from '../../../components/Icon'
import FormLabel from '../../FormLabel'
import {
  FormFieldOrientation,
  FormFieldOrientationValues,
} from '../../../types/form'

type InputStyleProps = {
  $isError?: boolean
  $isActive?: boolean
  $hasLabel?: boolean
  $isDisabled?: boolean
  $hasErrorMessage?: boolean
  $orientation?: FormFieldOrientationValues
  placeholder?: string
}

export const Container = styled.div<InputStyleProps>`
  display: flex;
  flex-flow: column;

  // disabled
  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      opacity: 0.8;
      &,
      & * {
        cursor: not-allowed;
      }
    `}

  ${({ $orientation }) =>
    $orientation &&
    $orientation === FormFieldOrientation.Horizontal &&
    css`
      flex-direction: row;
      gap: 2rem;
      align-items: center;
    `};
`

export const Label = styled(FormLabel)``

export const MainWrapper = styled.div``

export const Ghost = styled.span`
  ${({ theme }) => theme.textStyles.montserratSm};
  position: absolute;
  visibility: hidden;
  height: 0;
  overflow: hidden;
  padding: 1rem 3.6rem 0.8rem 1.2rem;
  font-weight: 600;
  white-space: nowrap;
`

export const InputContainer = styled.label<InputStyleProps>`
  width: 100%;
  display: flex;
  flex-direction: row;
  border: 1px solid ${({ theme }) => theme.colors.greyContour};
  border-radius: 1rem;
  position: relative;
  width: 100%;
  overflow: hidden;

  ${({ $isError, theme }) =>
    $isError === true &&
    css`
      border-color: ${theme.colors.error};
    `}

  ${({ $isActive }) =>
    $isActive === true &&
    css`
      border-color: transparent;
      background-color: ${({ theme }) => theme.colors.pattensBlue};
    `}

  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      border-color: ${({ theme }) => theme.colors.greyContour};
      background-color: ${({ theme }) => theme.colors.pureWhite};
    `}
`

export const DropDown = styled(Icon)<{ $isActive?: boolean }>`
  width: 1rem;
  position: absolute;
  z-index: 3;
  color: ${({ theme }) => theme.colors.greyContour};
  top: 50%;
  right: 1.2rem;
  transform: translateY(-50%);

  ${({ $isActive }) =>
    $isActive === true &&
    css`
      color: ${({ theme }) => theme.colors.bluePrimary};
    `}
`

export const selectStyles = css<InputStyleProps>`
  appearance: none;
  // Additional resets for further consistency
  background-color: transparent;
  border: none;
  margin: 0;
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  cursor: inherit;
  line-height: inherit;
  outline: none;
  position: relative;
  z-index: 2;

  ${({ theme }) => theme.textStyles.montserratSm};
  width: 100%;
  border: 0;
  color: ${({ theme }) => theme.colors.blueSecondaryBis};
  font-weight: 600;
  background: transparent;
  padding: 1rem 2.6rem 0.8rem 1.2rem;

  // disabled
  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      pointer-events: none;
    `}

  ${({ $isActive }) =>
    $isActive === true &&
    css`
      background-color: ${({ theme }) => theme.colors.pattensBlue};
      border-color: transparent;
      color: ${({ theme }) => theme.colors.bluePrimary};
      font-weight: 600;
    `}

  &::placeholder {
    opacity: 0.5;
  }

  // readonly
  &[readonly] {
    cursor: default;
  }

  &:focus {
    outline: none;
  }

  &[type='password'] {
    letter-spacing: 3px;
  }
`
export const Input = styled.select<InputStyleProps>`
  ${selectStyles}

  &:focus {
    outline: 0.1rem dotted #000;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &:focus-visible {
    outline: 0.1rem dotted #000;
  }
`

const HintMessage = styled.p`
  ${({ theme }) => theme.textStyles.montserratTextBase}
  margin-top: 0.5rem;
  padding-inline: 0.8rem;
  text-align: left;
`
export const HelpMessage = styled(HintMessage)`
  color: ${({ theme }) => theme.colors.bismark};
`
export const ErrorMessage = styled(HintMessage)`
  color: ${({ theme }) => theme.colors.error};
`
